/**
 * AdminCorp — bucket 6 of 6. Renders the design's four-module national
 * Corporate Events layout: National Pipeline (5-stage kanban), Corp Events
 * by Region, Top Deals · National, Commission Payouts.
 *
 * The national corp-events / inquiry pipeline has no source yet (data model
 * is a separate Project), so the whole surface renders the design layout as
 * an em-dash skeleton behind a single <MockedOverlay>, mirroring
 * RegionalCorp. It lights up when BON-154 lands.
 */
import { adaptKpis } from '../lib/kpiAdapter';
import { KpiStrip, MockedOverlay } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';

import { useAdminCorp } from './adminApi';

const EM_DASH = '—';
const PIPELINE_STAGES = [
  'Inquired',
  'Responded',
  'Invoice sent',
  'Deposit paid',
  'Headcount submitted',
] as const;
const ROWS = ['a', 'b', 'c', 'd'] as const;

const heading: React.CSSProperties = { fontSize: 13, letterSpacing: '0.04em', margin: 0 };
const subtle = 'var(--nb-subtle)';

function NationalPipelineCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          National Pipeline
        </h3>
        <span className="nb-eyebrow">{EM_DASH} value</span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
        {PIPELINE_STAGES.map((stage) => (
          <div
            key={stage}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              background: 'var(--nb-surface)',
              border: '1px solid var(--nb-border)',
              borderRadius: 8,
              padding: 10,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <i style={{ width: 8, height: 8, borderRadius: 999, background: subtle }} />
              <span style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--nb-ink)' }}>
                {stage}
              </span>
            </div>
            <span className="nb-tabular" style={{ fontSize: 22, fontWeight: 700, color: subtle }}>
              {EM_DASH}
            </span>
            <span style={{ fontSize: 11, color: subtle }}>{EM_DASH}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CorpByRegionCard() {
  return (
    <section
      className="nb-card"
      style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
    >
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          Corp Events by Region
        </h3>
      </div>
      <table className="nb-tbl">
        <thead>
          <tr>
            <th>Region</th>
            <th>Inquired</th>
            <th>Responded</th>
            <th>Confirmed</th>
            <th style={{ textAlign: 'right' }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r}>
              <td style={{ color: subtle, fontWeight: 700 }}>{EM_DASH}</td>
              <td className="nb-tabular" style={{ color: subtle }}>
                {EM_DASH}
              </td>
              <td className="nb-tabular" style={{ color: subtle }}>
                {EM_DASH}
              </td>
              <td className="nb-tabular" style={{ color: subtle }}>
                {EM_DASH}
              </td>
              <td className="nb-tabular" style={{ textAlign: 'right', color: subtle }}>
                {EM_DASH}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function TopDealsCard() {
  return (
    <section className="nb-card" style={{ flex: '0 0 auto' }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          Top Deals · National
        </h3>
        <span className="nb-eyebrow">by value</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ROWS.map((r) => (
          <div key={r} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: subtle }}>{EM_DASH}</div>
              <div style={{ fontSize: 10.5, color: subtle }}>{EM_DASH}</div>
            </div>
            <span
              className="nb-tabular nb-mono"
              style={{ fontSize: 13, fontWeight: 700, color: subtle }}
            >
              {EM_DASH}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

function CommissionPayoutsCard() {
  return (
    <section className="nb-card" style={{ flex: 1, minHeight: 0 }}>
      <div className="nb-card-hd">
        <h3 className="nb-display" style={heading}>
          Commission Payouts
        </h3>
        <span className="nb-eyebrow">next 60 days</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {ROWS.map((r) => (
          <div
            key={r}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 0',
              borderBottom: '1px dashed var(--nb-border)',
            }}
          >
            <span style={{ width: 50, fontSize: 11, fontWeight: 700, color: subtle }}>
              {EM_DASH}
            </span>
            <span className="nb-mono" style={{ width: 50, fontSize: 10, color: subtle }}>
              {EM_DASH}
            </span>
            <span style={{ flex: 1, fontSize: 11.5, color: subtle }}>{EM_DASH}</span>
            <span
              className="nb-tabular nb-mono"
              style={{ fontSize: 12.5, fontWeight: 700, color: subtle }}
            >
              {EM_DASH}
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function AdminCorp() {
  const q = useAdminCorp();
  if (q.isLoading) return <BucketLoading />;
  if (q.isError) return <BucketError error={q.error} />;
  if (!q.data) return null;
  const { data } = q.data;

  return (
    <MockedOverlay reason="Corp Events data isn’t wired up yet. Goes live once the corp-events model ships — tracked in BON-154.">
      <KpiStrip items={adaptKpis(data.kpis)} />
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.55fr) minmax(0, 1fr)',
          gap: 14,
          marginTop: 16,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <NationalPipelineCard />
          <CorpByRegionCard />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <TopDealsCard />
          <CommissionPayoutsCard />
        </div>
      </div>
    </MockedOverlay>
  );
}
